// The deposit bonus a Shop's promo poster advertises: its RUNNING deposit bonus campaign, if any.
// Whether to show it is the Shop's choice, kept on this device per kiosk URL.
import type { CampaignBonus } from '../../pages/depositBonusDisplay';

/** What the poster prints for a deposit bonus. */
export type PosterDepositBonus = {
  /** The bar across the top, e.g. "First deposit bonus". */
  label: string;
  /** The offer, e.g. "$10 BONUS" or "10% BONUS". */
  offer: string;
  /** Cap under a percentage offer, e.g. "up to $20". */
  cap: string | null;
  /** Smallest deposit that earns it, e.g. "Min. deposit $10". */
  minDeposit: string | null;
};

type RunningFields = Pick<CampaignBonus, 'status' | 'startsAt' | 'endsAt' | 'application'>;

function time(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : NaN;
}

/** Active, started, and not yet ended (no end date = open-ended). */
export function isCampaignRunning(campaign: RunningFields, now: number = Date.now()): boolean {
  if (campaign.status !== 'active') return false;
  const starts = time(campaign.startsAt);
  const ends = time(campaign.endsAt);
  if (Number.isNaN(starts) || Number.isNaN(ends)) return false;
  if (starts !== null && starts > now) return false;
  if (ends !== null && now >= ends) return false;
  return true;
}

/**
 * The campaign the poster shows: of the running ones, a first-deposit campaign first, then the one
 * ending soonest (an open-ended one last). Null when none is running.
 */
export function pickPosterCampaign<T extends RunningFields>(campaigns: readonly T[], now: number = Date.now()): T | null {
  const running = campaigns.filter((c) => isCampaignRunning(c, now));
  if (running.length === 0) return null;
  const endOf = (c: T) => time(c.endsAt) ?? Number.POSITIVE_INFINITY;
  const rank = (c: T) => (c.application === 'first_deposit' ? 0 : 1);
  return [...running].sort((a, b) => rank(a) - rank(b) || endOf(a) - endOf(b))[0];
}

/** Dollars as the poster prints them: "$10", "$12.50". */
export function posterMoney(amount: number): string {
  return `$${Number.isInteger(amount) ? String(amount) : amount.toFixed(2)}`;
}

function positive(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function depositBonusForPoster(
  campaign: Pick<CampaignBonus, 'application' | 'bonusType' | 'bonusValue' | 'minDeposit' | 'maxBonusPerDeposit'>
): PosterDepositBonus {
  const label = campaign.application === 'first_deposit' ? 'First deposit bonus' : 'Deposit bonus';
  const value = Number(campaign.bonusValue) || 0;
  const cap = positive(campaign.maxBonusPerDeposit);
  const min = positive(campaign.minDeposit);
  if (campaign.bonusType === 'percentage') {
    return {
      label,
      offer: `${Number(value.toFixed(2))}% BONUS`,
      cap: cap !== null ? `up to ${posterMoney(cap)}` : null,
      minDeposit: min !== null ? `Min. deposit ${posterMoney(min)}` : null,
    };
  }
  return {
    label,
    offer: `${posterMoney(value)} BONUS`,
    cap: null,
    minDeposit: min !== null ? `Min. deposit ${posterMoney(min)}` : null,
  };
}

const STORAGE_PREFIX = 'shop-poster-deposit-bonus:';

/** Whether the Shop wants the deposit bonus on this kiosk's poster (on unless switched off). */
export function loadPosterShowBonus(kioskUrl: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + kioskUrl) !== 'off';
  } catch {
    return true;
  }
}

export function savePosterShowBonus(kioskUrl: string, show: boolean): void {
  try {
    if (show) localStorage.removeItem(STORAGE_PREFIX + kioskUrl);
    else localStorage.setItem(STORAGE_PREFIX + kioskUrl, 'off');
  } catch {
    /* storage unavailable (private mode): the choice lasts for this visit only */
  }
}
